import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";

// Redux
import { connect } from "react-redux";

// Components
import IkTable from "@Components/IkTable";

// Utils
import dataHandler from "@Utils/dataHandler";
import { map } from "lodash";

// Styles
import styles from "./styles.module.css";

const Resumidas = ({ dispatch, userName, companyName, onOpenFilterModal, appliedFilters, hasGroups, isPromo }) => {
	const defaultParams = { page: 1, orderDir: "desc", rows: 20 };
	const [filteredItems, setFilteredItems] = useState([]);
	const [filteredCount, setFilteredCount] = useState(0);
	const [filteredPrintData, setFilteredPrintData] = useState([]);
	const [lastParams, setLastParams] = useState(defaultParams);
	const [sumData, setSumData] = useState({});

	// const groupBy = hasGroups ? "groupId" : "userId";

	useEffect(() => {
		const mergedParams = { ...defaultParams, ...appliedFilters };

		(async () => {
			try {
				const response = await dataHandler({
					dispatch,
					method: "GET",
					url: "/reports/combined/summary",
					params: mergedParams,
				});

				const transformedItems = map(response.items, (item) => {
					return {
						...item,
						tags: item.tags || [],
						group: item.group || {},
						nonPromoCount: (item.total || 0) - (item.promoCount || 0),
					};
				});

				setFilteredItems(transformedItems);
				setFilteredCount(response.count);
				setSumData({
					...response.total,
					nonPromoCount: (response.total?.total || 0) - (response.total?.promoCount || 0),
				});
				setLastParams(mergedParams);
			} catch (error) {
				setFilteredItems([]);
				setFilteredCount(0);
				setSumData({});
			}
		})();
	}, [appliedFilters, hasGroups]);

	const handlerData = async (params = defaultParams, printable) => {
		const mergedParams = {
			...params,
			...appliedFilters,
			fields: "user.fullName",
			// groupBy,
		};

		const data = await dataHandler({
			method: "GET",
			dispatch,
			url: "/reports/combined/summary",
			params: mergedParams,
		});

		const transformedItems = map(data.items, (item) => {
			return {
				...item,
				tags: item.tags || [],
				group: item.group || {},
				nonPromoCount: (item.total || 0) - (item.promoCount || 0),
			};
		});

		if (printable) {
			setFilteredPrintData(transformedItems);
		} else {
			setFilteredItems(transformedItems);
			setFilteredCount(data.count);
			setLastParams(mergedParams);
			setSumData({
				...data.total,
				nonPromoCount: (data.total?.total || 0) - (data.total?.promoCount || 0),
			});
		}
	};

	const handleRefreshData = () => {
		handlerData(lastParams);
	};

	const renderTopButtons = () => {
		const buttons = [
			{
				icon: "filter",
				className: Object.keys(appliedFilters).length > 0 ? styles.filterButtonActive : styles.filterButton,
				onClick: onOpenFilterModal,
				content: Object.keys(appliedFilters).length > 0 ? "Filtrando" : "",
				label: Object.keys(appliedFilters).length > 0 ? "Filtrando" : "",
				showLabel: true,
				componentType: "button",
			},
			{
				icon: "refresh",
				className: styles.refreshButton,
				onClick: handleRefreshData,
			},
		];

		const dateRange = appliedFilters.createdDate || appliedFilters.closeDate;
		if (dateRange) {
			const formatDate = (d) => {
				if (!d) return "";
				const parts = d.split("-");
				if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
				return d;
			};
			const [start, end] = dateRange.split(",");
			let value = formatDate(start);
			const today = new Date();
			const currentEnd =
				end || `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
			if (currentEnd && start !== currentEnd) value += ` al ${formatDate(currentEnd)}`;

			buttons.push({
				componentType: "Label",
				title: "Fecha:",
				value,
			});
		}

		return buttons;
	};

	const columns = [];

	if (hasGroups) {
		columns.push({
			label: "Grupo",
			key: "group.name",
			format: "string",
			headerPosition: "center",
			dataPosition: "left",
			width: 1,
		});
	}

	columns.push({
		label: "Usuario",
		key: "user.fullName",
		format: "string",
		headerPosition: "center",
		dataPosition: "left",
		width: 1,
	});

	columns.push({
		label: "Cant. Conversaciones",
		key: "total",
		format: "number",
		headerPosition: "center",
		dataPosition: "center",
		width: 1,
	});

	if (isPromo) {
		columns.push(
			{
				label: "Cant. Promo",
				key: "promoCount",
				format: "number",
				headerPosition: "center",
				dataPosition: "center",
				width: 1,
			},
			{
				label: "Conv. Directas",
				key: "nonPromoCount",
				format: "blankZero",
				headerPosition: "center",
				dataPosition: "center",
				width: 1,
				canFilter: false,
				canSort: false,
			}
		);
	}

	columns.push(
		{ label: "Cant. Mensaje", key: "messageCount", format: "number", headerPosition: "center", dataPosition: "center", width: 1 },
		{ label: "Tab Nuevo", key: "newCount", format: "blankZero", headerPosition: "center", dataPosition: "center", width: 1 },
		{ label: "Tab Atender", key: "agentCount", format: "blankZero", headerPosition: "center", dataPosition: "center", width: 1 },
		{ label: "Tab Cliente", key: "clientCount", format: "blankZero", headerPosition: "center", dataPosition: "center", width: 1 },
		{ label: "A Tiempo", key: "onTimeCount", format: "blankZero", headerPosition: "center", dataPosition: "center", width: 1 },
		{ label: "En Atraso", key: "delayedCount", format: "blankZero", headerPosition: "center", dataPosition: "center", width: 1 },
		{ label: "Vencido", key: "expiredCount", format: "blankZero", headerPosition: "center", dataPosition: "center", width: 1 },
		// { label: "IG", key: "igCount", format: "blankZero", headerPosition: "center", dataPosition: "center", width: 0.5 },
		// { label: "FB", key: "fbCount", format: "blankZero", headerPosition: "center", dataPosition: "center", width: 0.5 },
		{ label: "Plantillas", key: "templateCount", format: "blankZero", headerPosition: "left", dataPosition: "left", width: 15 }
	);

	return (
		<div className={styles.container}>
			<div className={styles.containerTableGroup}>
				<div className={styles.tableGroupList}>
					<IkTable
						data={filteredItems}
						columns={columns}
						totalRows={filteredCount}
						filteredPrintData={filteredPrintData}
						defaultParams={defaultParams}
						dataHandler={handlerData}
						print={{
							printHeader: {
								company: companyName,
								title: "Resumidas",
								user: userName,
							},
						}}
						searcher={{
							searchable: true,
							searchPosition: "flex-start",
							searchDisabled: false,
							filterButton: false,
							width: "40rem",
						}}
						topButtons={renderTopButtons()}
						sumData={sumData}
					/>
				</div>
			</div>
		</div>
	);
};

function mapStateToProps({ user, company }) {
	return {
		userName: user.name,
		companyName: company.name,
		hasGroups: !!company?.groups?.length || company.hasGroups,
		isPromo: company?.settings?.isPromo,
	};
}

Resumidas.propTypes = {
	companyName: PropTypes.string,
	userName: PropTypes.string,
	dispatch: PropTypes.func,
	onOpenFilterModal: PropTypes.func,
	appliedFilters: PropTypes.object,
	hasGroups: PropTypes.bool,
	isPromo: PropTypes.bool,
};

export default connect(mapStateToProps)(Resumidas);
